import { useState } from "react";
import { Link } from "wouter";
import { Menu, X } from "lucide-react";
import { m, AnimatePresence, useReducedMotion } from "framer-motion";
import { cn } from "../../lib/cn";
import { FOCUS_RING } from "../../lib/focusRing";
import { BrandWordmark } from "../BrandWordmark";
import { fadeUp, staggerContainer } from "../../lib/motion";
import { useStage } from "./stage/context";

const anchors = [
  { href: "#how", label: "How it works" },
  { href: "#security", label: "Privacy" },
  { href: "#faq", label: "FAQ" },
];

/**
 * Fixed over the full-screen scenes: clear while the hero is in place, then a
 * frosted bar. Over a dark scene it flips to light-on-dark so the wordmark and
 * links stay legible.
 */
export function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const prefersReduced = useReducedMotion();
  const { scenes, arrivedId } = useStage();
  const close = () => setMenuOpen(false);

  const atTop = arrivedId === null || arrivedId === scenes[0]?.id;
  const dark = !menuOpen && scenes.find((s) => s.id === arrivedId)?.tone === "dark";

  return (
    <m.header
      initial={prefersReduced ? false : { y: -100 }}
      animate={{ y: 0 }}
      transition={{ type: "spring", stiffness: 260, damping: 26 }}
      className={cn(
        "fixed inset-x-0 top-0 z-50 border-b transition-[background-color,box-shadow,border-color] duration-500",
        menuOpen
          ? "border-neutral-200/70 bg-white shadow-soft"
          : atTop
            ? "border-transparent bg-transparent"
            : dark
              ? "border-white/10 bg-slate-950/40 backdrop-blur-md"
              : "border-neutral-200/70 bg-white/80 shadow-soft backdrop-blur-md",
      )}
    >
      <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-3.5">
        <a href="#top" className={cn("inline-flex rounded-lg", FOCUS_RING)} aria-label="Leena's List — back to top">
          <m.span
            whileHover={prefersReduced ? undefined : { scale: 1.04 }}
            whileTap={prefersReduced ? undefined : { scale: 0.97 }}
            transition={{ type: "spring", stiffness: 400, damping: 14 }}
            className="inline-flex"
          >
            <BrandWordmark className="h-7" tone={dark ? "light" : "dark"} />
          </m.span>
        </a>

        <nav className="hidden items-center gap-1 text-sm sm:flex">
          {anchors.map((a) => (
            <a
              key={a.href}
              href={a.href}
              className={cn(
                "rounded-full px-3 py-2.5 font-medium transition-colors",
                dark
                  ? "text-slate-200 hover:bg-white/10 hover:text-white"
                  : "text-neutral-600 hover:bg-sky-50 hover:text-neutral-900",
                FOCUS_RING,
              )}
            >
              {a.label}
            </a>
          ))}
          <Link href="/login" asChild>
            <m.a
              whileHover={prefersReduced ? undefined : { y: -2 }}
              whileTap={prefersReduced ? undefined : { scale: 0.96 }}
              className={cn(
                "rounded-full px-4 py-2.5 font-medium transition-colors",
                dark ? "text-white hover:bg-white/10" : "text-neutral-700 hover:bg-sky-50",
                FOCUS_RING,
              )}
            >
              Sign in
            </m.a>
          </Link>
          <Link href="/register" asChild>
            <m.a
              whileHover={prefersReduced ? undefined : { y: -2 }}
              whileTap={prefersReduced ? undefined : { scale: 0.96 }}
              className={cn(
                "rounded-full bg-gradient-to-br from-sky-500 to-indigo-600 px-4 py-2.5 font-medium text-white shadow-soft transition-shadow hover:shadow-glow",
                FOCUS_RING,
              )}
            >
              Get started
            </m.a>
          </Link>
        </nav>

        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="Toggle menu"
          aria-expanded={menuOpen}
          className={cn(
            "inline-flex h-11 w-11 items-center justify-center rounded-full transition-colors sm:hidden",
            dark ? "text-white hover:bg-white/10" : "text-neutral-700 hover:bg-sky-50",
            FOCUS_RING,
          )}
        >
          <AnimatePresence mode="wait" initial={false}>
            {menuOpen ? (
              <m.span
                key="close"
                initial={{ rotate: -90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 90, opacity: 0 }}
                transition={{ duration: 0.18 }}
                className="inline-flex"
              >
                <X className="h-5 w-5" />
              </m.span>
            ) : (
              <m.span
                key="open"
                initial={{ rotate: 90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: -90, opacity: 0 }}
                transition={{ duration: 0.18 }}
                className="inline-flex"
              >
                <Menu className="h-5 w-5" />
              </m.span>
            )}
          </AnimatePresence>
        </button>
      </div>

      <AnimatePresence initial={false}>
        {menuOpen && (
          <m.nav
            key="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-neutral-200 bg-white sm:hidden"
          >
            <m.div variants={staggerContainer} initial="hidden" animate="show" className="px-6 py-2 text-sm">
              {anchors.map((a, i) => (
                <m.a
                  key={a.href}
                  href={a.href}
                  onClick={close}
                  variants={fadeUp}
                  custom={i * 0.05}
                  className={cn(
                    "flex min-h-[44px] items-center rounded-lg px-2 font-medium text-neutral-700 transition-colors hover:bg-sky-50",
                    FOCUS_RING,
                  )}
                >
                  {a.label}
                </m.a>
              ))}
              <m.div variants={fadeUp} custom={anchors.length * 0.05}>
                <Link
                  href="/login"
                  onClick={close}
                  className={cn(
                    "flex min-h-[44px] items-center rounded-lg px-2 font-medium text-neutral-700 transition-colors hover:bg-sky-50",
                    FOCUS_RING,
                  )}
                >
                  Sign in
                </Link>
              </m.div>
              <m.div variants={fadeUp} custom={(anchors.length + 1) * 0.05} className="pb-2 pt-1">
                <Link
                  href="/register"
                  onClick={close}
                  className={cn(
                    "flex min-h-[44px] items-center justify-center rounded-full bg-gradient-to-br from-sky-500 to-indigo-600 px-2 text-center font-medium text-white shadow-soft",
                    FOCUS_RING,
                  )}
                >
                  Get started
                </Link>
              </m.div>
            </m.div>
          </m.nav>
        )}
      </AnimatePresence>
    </m.header>
  );
}
